import { useCallback, useEffect } from 'react';
import type { EditorDocument } from './use-editor-document';
import type { Selection } from './use-selection';

/**
 * Arrow-key nudging.
 *
 * Every keypress in one burst is folded into a single history entry. Holding an arrow key
 * fires the handler dozens of times a second, and without coalescing an undo would step
 * back one repeat at a time — dozens of presses to reverse what felt like one movement. The
 * run ends on key-up, so a deliberate second tap is its own entry.
 *
 * The step is in the plan's own drawing units. There is no real-world scale anywhere in this
 * data, so it is expressed as a fraction of the plan's width and stays proportionate whether
 * a project's coordinates run 0–10 or 0–1000.
 */

const NUDGE_FRACTION_OF_PLAN = 0.0015;
const LARGE_STEP_MULTIPLIER = 5;

const ARROW_DELTAS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  // The Y axis points up in these drawings, so Up increases it — the opposite of screen space.
  ArrowUp: [0, 1],
  ArrowDown: [0, -1],
};

interface Props {
  readonly document: EditorDocument;
  readonly selection: Selection;
  /** Width of the plan in its own units, used to size the step. */
  readonly planWidth: number;
  readonly enabled: boolean;
  /**
   * Deletes the selection.
   *
   * Owned by the editor rather than done here, because deleting a bookable desk costs its
   * room a seat and the admin is asked before it happens. Doing the removal here instead
   * meant the Delete key skipped that question entirely, while the inspector's Delete button
   * asked it — the same action with two different outcomes depending on how it was reached.
   */
  readonly onDelete: () => void;
}

export const useNudge = ({
  document: editorDocument,
  selection,
  planWidth,
  enabled,
  onDelete,
}: Props): void => {
  const { move, endGesture } = editorDocument;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || !selection.keys.size) {
        return;
      }

      // A keystroke aimed at a text field is not a canvas command.
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        onDelete();
        return;
      }

      const direction = ARROW_DELTAS[event.key];
      if (!direction) {
        return;
      }

      event.preventDefault();

      const step = planWidth * NUDGE_FRACTION_OF_PLAN * (event.shiftKey ? LARGE_STEP_MULTIPLIER : 1);
      move(selection.keys, direction[0] * step, direction[1] * step, 'nudge');
    },
    [enabled, selection, planWidth, move, onDelete],
  );

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (ARROW_DELTAS[event.key]) {
        endGesture();
      }
    },
    [endGesture],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);
};

export default useNudge;
